/*
 * bc-customize-segment
 *
 * PDP → Segment Builder handoff (blocks/bc-customize-segment.liquid). The
 * anchor's static href is the bare builder page (the no-JS fallback); this
 * element rewrites it with the PDP's CURRENT config as builder URL params
 * (the same permalink format bc-segment-builder.js restores in _applyUrl):
 *
 *   w=<entry key>            selected variant → entry, via the block's JSON
 *                            map (color-flattened keys mirroring
 *                            bc-segment-webbing-json)
 *   mode=custom&len=&q=      live <bc-custom-length> length × pieces when
 *                            that component is active (or the map marks the
 *                            variant custom)
 *   val=<Length value>&q=    fixed-length variants (+ the form quantity)
 *   e1=<kind>&e2=<kind>      checked bc-end-treatment radios (data-kind);
 *                            pattern/opening/sleeve stay builder defaults —
 *                            the PDP doesn't choose those
 *
 * The href is synced on hover/focus (so middle/cmd-click and copy-link get
 * the config too) and recomputed on click for freshness.
 */
(() => {
  if (customElements.get('bc-customize-segment')) return;

  class BcCustomizeSegment extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const link = this.querySelector('[data-bc-customize]');
      if (!link) return;
      this._base = link.getAttribute('href');
      const sync = () => { link.href = this._buildUrl(); };
      this.addEventListener('pointerenter', sync);
      this.addEventListener('focusin', sync);
      this.addEventListener('touchstart', sync, { passive: true });
      link.addEventListener('click', sync);
    }

    _map() {
      try {
        const el = this.querySelector('script[data-bc-seg-map]');
        return JSON.parse(el ? el.textContent : '{}') || {};
      } catch (_e) {
        return {};
      }
    }

    _buildUrl() {
      const scope = this.closest('.shopify-section') || document;
      const params = new URLSearchParams();

      const form = document.getElementById(this.dataset.formId)
        || scope.querySelector('product-form-component form');
      const idInput = form && form.querySelector('[name="id"]');
      const entry = idInput
        ? ((this._map().variants || {})[String(idInput.value)] || null)
        : null;
      if (entry) params.set('w', entry.key);

      // Length: the live custom-length component wins (it knows whether the
      // selected variant activates it); the map's `custom` flag covers the
      // component being absent/mid-morph.
      const bcl = scope.querySelector('bc-custom-length');
      const bclActive = !!(bcl && typeof bcl._isActive === 'function' && bcl._isActive());
      if (bclActive || (entry && entry.custom)) {
        params.set('mode', 'custom');
        const len = bclActive && typeof bcl._readLength === 'function' ? bcl._readLength() : 0;
        if (len > 0) params.set('len', String(len));
        const pieces = bclActive && typeof bcl._readPieces === 'function' ? bcl._readPieces() : 1;
        if (pieces > 1) params.set('q', String(pieces));
      } else if (entry && entry.val) {
        params.set('val', entry.val);
        const qtyInput = form && form.querySelector('input[name="quantity"]');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        if (qty > 1) params.set('q', String(qty));
      }

      // End treatments: kind-only tokens — the builder fills its own
      // pattern/opening/sleeve defaults for the kind.
      [['end1', 'e1'], ['end2', 'e2']].forEach(([role, param]) => {
        const checked = scope.querySelector(`[data-bc-end-role="${role}"] input:checked`);
        if (checked && checked.dataset.kind) params.set(param, checked.dataset.kind);
      });

      const qs = params.toString();
      if (!qs) return this._base;
      return this._base + (this._base.indexOf('?') === -1 ? '?' : '&') + qs;
    }
  }

  customElements.define('bc-customize-segment', BcCustomizeSegment);
})();
